const BASE = "";

/** Thrown when data feeds are unhealthy (503 from feed health middleware) */
export class FeedHealthError extends Error {
  feedHealth: any;
  constructor(message: string, feedHealth: any) {
    super(message);
    this.name = "FeedHealthError";
    this.feedHealth = feedHealth;
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  let json: any;
  try {
    json = await res.json();
  } catch {
    // Non-JSON body — e.g. an nginx 502/504 HTML page during a deploy/restart
    throw new Error("The server is temporarily unavailable. Please try again in a moment.");
  }

  // Session missing/expired — AuthContext clears the user, ProtectedRoute
  // then redirects to /login. (Failed logins keep their own server message.)
  if (res.status === 401) {
    window.dispatchEvent(new Event("auth:unauthorized"));
    throw new Error(json.error || "Your session has expired. Please log in again.");
  }

  // Detect feed health errors (503 with error: "feed_health_error")
  if (json.error === "feed_health_error") {
    throw new FeedHealthError(json.message, json.feedHealth);
  }

  if (!json.ok) throw new Error(json.error);
  return json.data;
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: "include" });
  return handleResponse<T>(res);
}

export async function apiPost<T>(path: string, body?: Record<string, unknown> | FormData): Promise<T> {
  const isFormData = body instanceof FormData;
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: isFormData ? undefined : { "Content-Type": "application/json" },
    body: isFormData ? body : body ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(res);
}

/** POST with form-urlencoded body (for login) */
export async function apiPostForm<T>(path: string, body: Record<string, string>): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(body).toString(),
  });
  return handleResponse<T>(res);
}

export async function apiPut<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

export async function apiDelete<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "DELETE",
    credentials: "include",
  });
  return handleResponse<T>(res);
}

export async function apiPatch<T>(path: string, body?: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "PATCH",
    headers: body ? { "Content-Type": "application/json" } : {},
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(res);
}
